import { inject, Injectable } from '@angular/core';
import { HelperTimeService } from '@common/services/helper-time.service';
import { MetaNeo } from '@fichaje/interfaces/ficha.interface';
import { EventActionButton, EventNeoTyped, EventStates, FichaAlert, FichajeActionButtons, FichaSettings, FichaStates } from '../interfaces/ficha-event.interface';
import { FichaStatesBuilderRulesService } from './ficha-states-builder-rules.service';
import { KIOSK_FULL_DAY_PERMISSION_ENABLED } from '@common/constants/feature-flags.const';

@Injectable({
  providedIn: 'root',
})
export class FichaStatesBuilderService {
  private readonly helpTime = inject(HelperTimeService);
  private readonly rules = inject(FichaStatesBuilderRulesService);

  // Public API kept compatible with the original builder service.
  buildFichajeStates(evTyped: EventNeoTyped[], meta: MetaNeo, settings: FichaSettings, eventStates: EventStates): FichaStates {
    const events = structuredClone(evTyped ?? []);
    const metaSafe = structuredClone(meta ?? ({} as MetaNeo));

    const eventInProgressArr: number[] = [];
    const eventPendingArr: number[] = [];

    events.forEach((event, index) => {
      if (this.rules.isEventInProgress(event, settings)) {
        eventInProgressArr.push(index);
      }
      if (this.rules.isEventPending(event)) {
        eventPendingArr.push(index);
      }
    });

    const eventInProgressIndex = eventInProgressArr.length > 0 ? eventInProgressArr[0] : -1;
    const eventInProgressObj = eventInProgressIndex >= 0 ? events[eventInProgressIndex] : undefined;

    const build: FichaStates = {
      eventInProgressIndex,
      eventInProgressArr,
      eventPendingArr,
      eventInProgressObj,
      eventInProgressActionButton: 'none',
      fichaAlerts: [],
    };

    build.meta = this.buildMetaStates(metaSafe);
    build.fichajeActionButtons = this.setFichaActionButtons(events, settings, build, eventStates);
    build.eventInProgressActionButton = this.setEventActionButton(events, settings, build, eventStates);
    build.closestEventToNowId = settings.whenIsIt === 'today' ? this.closestPendingEventToNow(events) : undefined;
    build.fichaAlerts = undefined //this.setFichaAlerts(metaSafe, settings, build);

    build.eventInProgressRemoteWork = eventInProgressObj?.remote_work;

    return build;
  }

  private buildMetaStates(meta: MetaNeo): FichaStates['meta'] {
    if (!meta) {
      return undefined;
    }

    return {
      horasExtrasActivas: meta.open_overtime == null ? undefined : meta.open_overtime,
      permisosDelDiaActivos: meta.open_leave == null ? undefined : meta.open_leave,
      pausasActivas: meta.open_pause == null ? undefined : meta.open_pause,
      opcionales: meta.meta_v2?.opcionales ?? null,
    };
  }

  private setEventActionButton(
    eventos?: EventNeoTyped[],
    settings?: FichaSettings,
    states?: FichaStates,
    eventStates?: EventStates,
  ): EventActionButton {
    let selected: EventActionButton = 'none';

    if (!eventos || !settings || !states || !eventStates) {
      return selected;
    }

    if (eventStates.eventExpandedId && states.eventInProgressIndex < 0 && settings.whenIsIt === 'today') {
      const eventExpanded = eventos.find((event) => `id${event.id}` === eventStates.eventExpandedId);
      if (eventExpanded && this.rules.canStartExpandedEvent(eventExpanded, settings)) {
        selected = 'workStart';
      }
    }

    if (states.eventInProgressIndex < 0 || !eventos[states.eventInProgressIndex]) {
      return selected;
    }

    const event = eventos[states.eventInProgressIndex];

    if (this.showExtraHourEndButton(event, settings, states)) {
      selected = 'horaExtraEnd';
    } else if (this.showPausaEndButton(event, settings, states)) {
      selected = 'pausaEnd';
    } else if (this.showPermisoEndButton(event, settings, states)) {
      selected = 'permisoEnd';
    } else if (this.rules.canShowWorkEnd(event, settings)) {
      selected = 'workEnd';
    } else if (this.rules.canShowNewEventIn(event, settings)) {
      selected = 'newEventIn';
    } else if (this.rules.canShowHoraExtraIn(event, settings)) {
      selected = 'horaExtraIn';
    }

    return selected;
  }

  private showExtraHourEndButton(event: EventNeoTyped, settings: FichaSettings, states: FichaStates): boolean {
    return settings.whenIsIt === 'today' && !!states.meta?.horasExtrasActivas && states.meta.horasExtrasActivas.schedule_id === event.id;
  }

  private showPausaEndButton(event: EventNeoTyped, settings: FichaSettings, states: FichaStates): boolean {
    return settings.whenIsIt === 'today' && !!states.meta?.pausasActivas && +states.meta.pausasActivas.schedule_id === event.id;
  }

  private showPermisoEndButton(event: EventNeoTyped, settings: FichaSettings, states: FichaStates): boolean {
    return settings.whenIsIt === 'today' && !!states.meta?.permisosDelDiaActivos && +states.meta.permisosDelDiaActivos.schedule_id === event.id;
  }

  private setFichaActionButtons(
    eventos?: EventNeoTyped[],
    settings?: FichaSettings,
    states?: FichaStates,
    eventStates?: EventStates,
  ): FichajeActionButtons | undefined {
    if (!eventos || !settings || !states) {
      return undefined;
    }

    const eventInProgress = states.eventInProgressIndex >= 0 ? eventos[states.eventInProgressIndex] : undefined;
    const inProgressStartInFutureOrNow = this.rules.hasInProgressStartInFutureOrNow(eventInProgress);
    if (inProgressStartInFutureOrNow) {
      return undefined;
    }


    const build: FichajeActionButtons = {
      showNewEventButton: false,
      showNewHoraExtraButton: false,
      showNewPauseButton: false,
      showPermisoButton: false,
      showFullDayPermisoButton: false,
      showEventsListButton: { showList: false, pendingEvents: 0 },
    };

    build.showNewEventButton = this.showNewEventButton(settings, states);
    build.showNewHoraExtraButton = this.showHoraExtraButton(eventos, settings, states);
    build.showNewPauseButton = this.showNewPauseButton(settings, states);
    build.showPermisoButton = this.showPermisoButton(settings, states);
    build.showFullDayPermisoButton = this.showFullDayPermisoButton(settings, states);
    build.showEventsListButton = this.showEventsListButton(eventos, states, eventStates);

    

    const hasAnyButton =
      build.showNewEventButton ||
      build.showNewHoraExtraButton ||
      build.showNewPauseButton ||
      build.showPermisoButton ||
      // Mientras esté oculto (feature flag) no debe hacer aparecer la barra de acciones vacía.
      (settings.isStation && KIOSK_FULL_DAY_PERMISSION_ENABLED && build.showFullDayPermisoButton) ||
      build.showEventsListButton.showList;

    return hasAnyButton ? build : undefined;
  }

  private showNewEventButton(settings: FichaSettings, states: FichaStates): boolean {
    return (
      states.eventInProgressIndex === -1 &&
      settings.whenIsIt === 'today' &&
      (settings.userLogged?.trabajoLibre || settings.userLogged?.fichajeSinAgenda)
    );
  }

  private showHoraExtraButton(eventos: EventNeoTyped[], settings: FichaSettings, states: FichaStates): boolean {
    const hasNoEventsOrWork = eventos.length === 0 || eventos.some((event) => event.type === 'work');

    const baseRules =
      settings.userLogged?.mostrarHorasExtras &&
      settings.whenIsIt === 'today' &&
      states.eventInProgressIndex < 0 &&
      hasNoEventsOrWork;

    return baseRules && this.rules.canShowOvertimeBlock(eventos, settings, states);
  }

  private showFullDayPermisoButton(settings: FichaSettings, states: FichaStates): boolean {
    return (
      !!settings.userLogged?.mostrarPermisosRetribuidos &&
      settings.whenIsIt === 'today' &&
      states.eventInProgressIndex < 0 &&
      states.meta?.permisosDelDiaActivos == undefined
    );
  }

  private showPermisoButton(settings: FichaSettings, states: FichaStates): boolean {
    return (
      !!settings.userLogged?.mostrarPermisosRetribuidos &&
      settings.whenIsIt === 'today' &&
      states.eventInProgressIndex >= 0 &&
      states.meta?.horasExtrasActivas == undefined &&
      states.meta?.permisosDelDiaActivos == undefined &&
      states.meta?.pausasActivas == undefined
    );
  }

  private showNewPauseButton(settings: FichaSettings, states: FichaStates): boolean {
    return (
      !!settings.userLogged?.mostrarPausas &&
      settings.whenIsIt === 'today' &&
      states.eventInProgressIndex >= 0 &&
      states.meta?.horasExtrasActivas == undefined &&
      states.meta?.permisosDelDiaActivos == undefined &&
      states.meta?.pausasActivas == undefined &&
      (settings.isStation || settings.userLogged?.acceso_web)
    );
  }

  private showEventsListButton(
    eventos: EventNeoTyped[],
    states: FichaStates,
    eventStates?: EventStates,
  ): { showList: boolean; pendingEvents: number } {
    const evInProgress = states.eventInProgressIndex >= 0 ? eventos[states.eventInProgressIndex] : undefined;
    const eventExpandedExistsInList = !!eventStates?.eventExpandedId &&
      eventos.some(e => `id${e.id}` === eventStates!.eventExpandedId);
    const showList = !evInProgress && eventos.length > 0 && eventExpandedExistsInList;
    const pendingEvents = states.eventPendingArr?.length ?? 0;
    return { showList, pendingEvents };
  }

  // private setFichaAlerts(meta: MetaNeo, settings: FichaSettings, states: FichaStates): FichaAlert[] {
  //   const eventoAnteriorSinFichaje = meta?.previous_work_day_without_clocking;
  //   const jornadaAnterior = meta?.previous_work_day;

  //   if (!(states.eventInProgressIndex < 0 && eventoAnteriorSinFichaje && settings.whenIsIt === 'today')) {
  //     return [];
  //   }

  //   const date = new Date(jornadaAnterior ? jornadaAnterior.event_start_expected : '');
  //   const message = jornadaAnterior === null ? '' : this.helpTime.dateToLocalePretty(date);

  //   return [
  //     {
  //       colorHex: '#c90e0a',
  //       borderStyle: 'solid',
  //       message: `Evento del día ${message} no fichado`,
  //       actions: [
  //         {
  //           label: 'Revisar día',
  //           nevigate: ['fichaje', this.helpTime.dateToStringDayISO(date)],
  //           open: '',
  //         },
  //       ],
  //     },
  //   ];
  // }

  private closestPendingEventToNow(eventos?: EventNeoTyped[]): string | undefined {
    if (!eventos || eventos.length === 0) {
      return undefined;
    }

    const nowInSecondsFrom1970 = Math.floor(Date.now() / 1000);
    let difference = 0;
    let closerEventId: string | number | undefined;

    eventos.forEach((event) => {
      if (!event.event_start_expected || event.event_end_clock_out || !event.event_end_expected) {
        return;
      }

      const dateStartSecondsFrom1970 = Math.floor(new Date(event.event_start_expected).getTime() / 1000);
      const dateEndSecondsFrom1970 = Math.floor(new Date(event.event_end_expected).getTime() / 1000);

      if (dateStartSecondsFrom1970 > nowInSecondsFrom1970) {
        setDifferenceToFuture(dateStartSecondsFrom1970);
      } else if (dateEndSecondsFrom1970 > nowInSecondsFrom1970) {
        setDifferenceToFuture(dateEndSecondsFrom1970);
      }

      function setDifferenceToFuture(future: number, now = nowInSecondsFrom1970): void {
        const distance = future - now;
        if (closerEventId === undefined || distance < difference) {
          difference = distance;
          closerEventId = event.id;
        }
      }
    });

    return closerEventId !== undefined ? `id${closerEventId}` : undefined;
  }

}
